import { getRelationMetadata } from '../metadata';
import http from '../http';
import getQueryUrl from '../http/getQueryUrl';
import network from '../network';
import database from '../database';
import getGlobalConfig from '../globalConfig';
import logging from '@sstdev/lib_logging';
import eventSink from '../eventSink';
import notificationTypes from '../constants/notificationTypes';

const { minFullTextSearchTermLength } = getGlobalConfig();
const _p = { network, http, database, getRelationMetadata, getQueryUrl, eventSink, now: () => Date.now() };

// A search box calls preFilter on every keystroke past the minimum length, so a broken server
// would otherwise toast on every one of them. One toast per relation per this interval is enough
// to tell the user what they are looking at; the log line still fires every time.
const FALLBACK_NOTICE_INTERVAL_MS = 60 * 1000;
const lastFallbackNoticeAt = new Map();
export default {
    getFilter,
    getUriComponent,
    fromHNode,
    pageResetRequired: true,
    preFilter,
    getMql,
    _private: _p
};

function fromHNode() {
    return getFilter();
}

function getFilter(searchTerm = '') {
    return {
        searchTerm
    };
}

function getUriComponent(filters, limitSyncSize) {
    const { fullTextSearch: { searchTerm = '' } = {}, fields = ['_id'] } = filters;
    if (searchTerm === '') {
        return '';
    }
    let url = `searchTerm=${encodeURIComponent(searchTerm)}`;
    // If this relation has a limitSyncSize property, then return the full record so that
    // it will be merged into the local database (where it might not already exist).
    // Otherwise, we just need the _ids to match local records.
    if (!limitSyncSize) {
        url += `&fields=${encodeURIComponent(JSON.stringify(fields))}`;
    }
    // While propertiesToSearch is used in getMql(), it is not processed here.
    // Instead, it is added to the URL in propertiesToSearch.js.
    return url;
}

async function preFilter(filters, filterId, lokiView) {
    const { paged: { page = 0 } = {}, fullTextSearch: { searchTerm } = {} } = filters;
    if (searchTerm == null) return;

    const bbFilterType = 'fullTextSearch';
    const combinedFilterId = filterId + bbFilterType;
    const { namespace, relation } = lokiView;
    const { limitSyncSize, supportsFullTextSearch } = _p.getRelationMetadata(namespace, relation);

    const { isOnline } = await _p.network.getStatus();
    // This used to require isDeployed() because local MongoDB could not serve search indexes at
    // all. The local stack runs Atlas Local now, so the deciding question is whether the server
    // can actually answer — which is what the catch below settles, per environment, at runtime.
    if (isOnline && searchTerm?.length >= minFullTextSearchTermLength && supportsFullTextSearch) {
        const modifiedFilters = { ...filters };
        delete modifiedFilters.propertiesToSearch;
        if (modifiedFilters.paged == null) {
            throw new Error('A paged filter is required for an online fullTextSearch.');
        }
        // Get _ids from server and add filter to lokiView for _ids
        const url = _p.getQueryUrl(namespace, relation, modifiedFilters, filterId);

        let serverResult;
        try {
            serverResult = await _p.http.get(url, true);
        } catch (error) {
            // A server that cannot answer this search must not take the search box down with it.
            // The usual cause is an environment whose search index has not been created yet, or
            // one whose index predates a field the query names — Atlas fails the whole
            // aggregation in that case rather than ignoring the field. Leaving serverResults
            // unset sends getMql down its local regex path, which is what this filter did before
            // full text search existed.
            //
            // That fallback must not be silent. A local-only answer over a size-limited relation is
            // a subset of the tenant's records under a grid that says "End of Records" — the exact
            // symptom that was reported as data loss (#1927) — so the user is told, once, that
            // this is what they are looking at.
            // Discard anything an earlier page already stored. `getMql` routes to the server path
            // whenever `serverResults` holds ANY page, so if page 0 succeeded and this page failed,
            // leaving page 0 behind pins the view to a stale subset — and raises a toast telling the
            // user the local fallback ran when it did not. Clearing both copies is what actually
            // sends `getMql` down its local regex path.
            const failedFilter = filters[bbFilterType];
            if (failedFilter) delete failedFilter.serverResults;
            const pipelineFilter = lokiView.filterPipeline?.find(f => f.uid === combinedFilterId)?.bbFilter;
            if (pipelineFilter) delete pipelineFilter.serverResults;
            logging.error(
                `[FULLTEXTSEARCH] ${namespace}:${relation} server search failed; ` +
                    `falling back to the local search. ${error?.message ?? error}`
            );
            notifyFallingBackToLocalSearch(namespace, relation);
            return;
        }

        const idsFound = serverResult?.items.map(i => i._id) || [];
        const bbFilter = filters[bbFilterType];
        // start with any previous server lookup results (if any)
        // Merge existing results from the filters object (which may come from getFiltersFromView with a
        // different filterId) with any results found in the filterPipeline for this specific filterId
        bbFilter.serverResults = {
            ...(bbFilter.serverResults ?? {}),
            ...(lokiView.filterPipeline.find(f => f.uid === combinedFilterId)?.bbFilter?.serverResults ?? {})
        };
        // add this page of results
        bbFilter.serverResults[`page${page}`] = idsFound;
        // If this is a limitSyncSize relation, then the server will return the whole record
        // (because they might not exist locally), so insert them locally.
        if (limitSyncSize) {
            const records = serverResult?.items ?? [];
            if (!records || records.length === 0) return;
            // Tag each record with filterId — consumed by the Phase 3 display gate (#332).
            // Records are permanently retained locally; eviction is handled by truncation
            // with active-view protection (see ADR-GF-001).
            records.forEach(record => {
                record.meta.forTempQueryResult = filterId;
            });

            const db = _p.database.get();
            await db.bulkUpsert(namespace, relation, records);
        }
    }
}

/**
 * Tell the user the server could not answer the search and only the records already on this
 * device were searched. Throttled per relation so a search box does not raise a toast per
 * keystroke. `addToList` keeps it in the notification list after the toast is gone.
 */
function notifyFallingBackToLocalSearch(namespace, relation) {
    const key = `${namespace}:${relation}`;
    const now = _p.now();
    const last = lastFallbackNoticeAt.get(key) ?? 0;
    if (now - last < FALLBACK_NOTICE_INTERVAL_MS) return;
    lastFallbackNoticeAt.set(key, now);

    const [, publish] = _p.eventSink();
    publish(
        {
            message:
                'The server search is unavailable, so only the records already on this device were searched. ' +
                'These results may be incomplete.',
            timeout: 8000,
            addToList: true,
            variant: 'warning',
            type: notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}

function getMql(filters) {
    const {
        fullTextSearch,
        propertiesToSearch,
        caseSensitive = false,
        exactMatchOnly = false,
        preferLocal = false
    } = filters;

    if (fullTextSearch == null || fullTextSearch.searchTerm == null || fullTextSearch.searchTerm === '') return;

    // If we have full text search results from the server, use those.
    if (!preferLocal && Object.keys(fullTextSearch.serverResults ?? {}).length > 0) {
        // Gather all server _id results into one array.
        const $in = Object.values(fullTextSearch.serverResults).reduce((prev, current) => prev.concat(current), []);
        return { _id: { $in } };
    }

    if (propertiesToSearch?.propertiesToSearch == null) {
        throw new Error('propertiesToSearch must be specified when performing a local fullTextSearch.');
    }

    //regex taken from https://stackoverflow.com/a/9310752/8575651
    const escapedString = fullTextSearch.searchTerm.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    // If we have a splitChar, then we need to search for each term separately.
    if (fullTextSearch.splitChar && escapedString.includes(fullTextSearch.splitChar)) {
        // split the search term, and trim any leading or trailing whitespace
        const terms = escapedString.split(fullTextSearch.splitChar).map(s => s.trim());
        // We want ALL the terms to be present in the result, so we use $and.
        return {
            // but we only need 1 property to contain the term, so per term we use $or.
            $and: terms.map(term => getOr(propertiesToSearch, term, { caseSensitive, exactMatchOnly }))
        };
    }
    //otherwise, we just need one of the properties to contain the entire term (use $or)
    return getOr(propertiesToSearch, escapedString, { caseSensitive, exactMatchOnly });
}

function getOr(propertiesToSearch, escapedString, options) {
    const { caseSensitive, exactMatchOnly } = options;
    const $or = propertiesToSearch.propertiesToSearch
        .filter(x => x)
        .map(key => {
            let $regex = [escapedString];
            if (caseSensitive) {
                if (exactMatchOnly) {
                    return { [key]: escapedString };
                }
            } else if (exactMatchOnly) {
                $regex = [`^${$regex[0]}$`, 'i'];
            } else {
                $regex.push('i');
            }

            return { [key]: { $regex } };
        });
    if ($or.length === 1) {
        return $or[0];
    }
    return { $or };
}
